<template>
  <div :id="`ticket-whatsapp-log-${whatsappLog.id}`">
    <loading-screen :is-loading="isSaving"></loading-screen>
    <errors :error-list="serverErrors"></errors>
    <bookmark-modal modelType="TicketWhatsappLog" :modelId="whatsappLog.id"
      :currentValue="whatsappLog.bookmark"></bookmark-modal>

    <transfer-modal
      :modal-id="`transfer-modal-whatsapp-log-${whatsappLog.id}`"
      entry-type="ticket_whatsapp_log"
      :entry-id="whatsappLog.id"
      @transferred="handleTransfer"
    />

    <div class="card shadow-none">
      <div class="card-body" :class="{ 'pb-0': collapsed }">
        <!-- tl-wrapper -->
        <div class="tl-wrapper tl-header">
          <div class="tl-l">
            <div class="avatar-xs">
              <span
                class="
                  avatar-title
                  rounded
                  bg-soft-primary
                  text-primary
                  font-size-16
                "
              >
                {{ whatsappLog.agent ? whatsappLog.agent.name.charAt(0) : 'ITG' }}
              </span>
            </div>
          </div>
          <div class="tl-r">
            <div>
              <span class="name">{{ whatsappLog.agent ? whatsappLog.agent.name : 'ITG' }}</span> sent a WhatsApp message
            </div>
            <small>
              <i>{{ displayTimestamp(whatsappLog.created_at) }}</i>
              <span v-if="whatsappLog.include_in_incident_report && whatsappLog.incident_report_timestamp" class="ml-2" style="color: #dc3545;">
                <i class="fa fa-clock-o mr-1"></i>
                Incident Report: {{ displayTimestamp(whatsappLog.incident_report_timestamp) }}
              </span>
              <split-tracking-badge v-if="whatsappLog.split_tracking" :split-tracking="whatsappLog.split_tracking" />
            </small>
          </div>
          <div class="tl-actions" v-if="!disableActions">
            <a
              href="#"
              class="btn btn-link text-muted"
              @click.prevent="toggleCollapsed"
            >
              <i
                class="fa"
                :class="{
                  'fa-chevron-down': collapsed,
                  'fa-chevron-up': !collapsed,
                }"
              ></i>
            </a>

              <!-- Incident Report Badge - only show if there's an incident report -->
              <a v-if="incidentReport" href="#" class="btn btn-link text-muted pl-1 pr-1" @click.prevent="toggleIncidentReportInclusion">
              <span class="badge" :class="{
                'badge-success': whatsappLog.include_in_incident_report,
                'badge-secondary': !whatsappLog.include_in_incident_report
              }" style="font-size: 10px; padding: 5px 9px;">
                <i v-if="whatsappLog.include_in_incident_report" class="fa fa-check-circle text-white mr-1"></i>
                <i v-else class="fa fa-times-circle text-white mr-1"></i>
                {{ whatsappLog.include_in_incident_report ? 'Included in Incident Report' : 'Excluded from Incident Report' }}
              </span>
            </a>

            <a href="#" class="btn btn-link text-muted pl-1 pr-1" @click.prevent="openBookmarkModal">
              <i class="fa fa-heart" :class="{
                'text-danger': whatsappLog.bookmark
              }"></i>
            </a>

            <b-dropdown
              variant="white"
              right
              toggle-class="text-muted pt-0 pr-0 pb-0"
            >
              <template v-slot:button-content>
                <i class="mdi mdi-dots-horizontal font-size-18"></i>
              </template>
              <b-dropdown-item @click="openTransferModal">
                Transfer to other ticket
              </b-dropdown-item>
              <b-dropdown-item @click="requestDelete" variant="danger"
                >Delete</b-dropdown-item
              >
            </b-dropdown>
          </div>
        </div>
        <!-- / tl-wrapper -->
        <!-- tl-wrapper -->
        <div class="tl-wrapper tl-header" v-show="!collapsed">
          <div class="tl-l"></div>
          <div class="tl-r">
            <b class="mr-2">From:</b>
            <span class="mr-2">{{ whatsappLog.from }}</span>
          </div>
        </div>
        <!-- / tl-wrapper -->
        <!-- tl-wrapper -->
        <div class="tl-wrapper tl-header" v-show="!collapsed">
          <div class="tl-l"></div>
          <div class="tl-r">
            <b class="mr-2">To:</b>
            <span class="mr-2" v-for="(number, index) in toAddresses" :key="index">{{ number }}</span>
          </div>
        </div>
        <!-- / tl-wrapper -->
          <!-- tl-wrapper -->
        <div class="tl-wrapper tl-header" v-show="!collapsed">
          <div class="tl-l"></div>
          <div class="tl-r">
            <b class="mr-2">Status:</b>
            <span class="mr-2">
              <template v-if="whatsappLog.status === 'sent'">
                <span class="badge badge-info font-size-12">
                  <i class="mdi mdi-clock-outline"></i> Sent - Pending Delivery Confirmation
                </span>
              </template>
              <template v-else-if="whatsappLog.status === 'delivered'">
                <span class="badge badge-success font-size-12">
                  <i class="mdi mdi-check-circle"></i> Delivered
                </span>
              </template>
              <template v-else-if="whatsappLog.status === 'failed' || whatsappLog.status === 'undelivered'">
                <span class="badge badge-danger font-size-12">
                  <i class="mdi mdi-close-circle"></i> Failed - likely because the conversation window has expired. Try sending an SMS instead.
                </span>
              </template>
              <template v-else>
                <span class="badge badge-info font-size-12">
                  {{ whatsappLog.status }}
                </span>
              </template>
            </span>
          </div>
        </div>
        <!-- / tl-wrapper -->
        <!-- tl-wrapper -->
        <div class="tl-wrapper" v-show="!collapsed">
          <div class="tl-l"></div>
          <div class="tl-r tl-r-message-body">
            {{ whatsappLog.body }}

            <!-- Add media preview -->
            <div v-if="whatsappLog.media_url" class="media-preview mt-2">
              <div class="media-item">
                <img 
                  v-if="!isPdf(whatsappLog.media_url)"
                  :src="whatsappLog.media_url" 
                  class="img-thumbnail cursor-pointer" 
                  style="max-height: 200px; max-width: 200px;"
                  @click="openImage(whatsappLog.media_url)"
                >
                <div
                  v-else
                  class="pdf-icon-container cursor-pointer"
                  @click="openImage(whatsappLog.media_url)"
                >
                  <i class="fa fa-file-pdf text-danger" style="font-size: 30px;"></i>
                  <div class="text-muted small mt-1">PDF Document</div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <!-- / tl-wrapper -->
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import Dates from "../../../../../mixins/Dates";
import Forms from "../../../../../mixins/Forms";
import axios from "axios";
import eventBus from "../../../../../event-bus";
import BookmarkModal from './BookmarkModal.vue';
import SplitTrackingBadge from '../../../../../components/SplitTrackingBadge.vue';
import TransferModal from './TransferModal.vue';

export default {
  props: ["whatsappLog", "disableActions"],
  name: "TicketWhatsappLog",

  data() {
    return {
      collapsed: false,
      isSaving: false,
      serverErrors: [],
    };
  },

  created() {
    eventBus.$on("collapse-all", () => {
      this.collapsed = true;
    });
    eventBus.$on("expand-all", () => {
      this.collapsed = false;
    });
    
    // Add listener for WhatsApp status updates
    eventBus.$on("ticketWhatsappLogUpdated", (e) => {
      // Check if this update is for the current WhatsApp log
      if (e.ticketWhatsappLogId === this.whatsappLog.id) {
        // Update the status locally
        this.whatsappLog.status = e.newStatus;
      }
    });
  },

  beforeDestroy() {
    eventBus.$off("collapse-all");
    eventBus.$off("expand-all");
    eventBus.$off("ticketWhatsappLogUpdated");
  },

  computed: {
    ...mapGetters("ticketViewer", {
      ticket: "ticket",
      incidentReport: "incidentReport",
    }),

    toAddresses() {
      return JSON.parse(this.whatsappLog.to);
    },
  },

  components: {BookmarkModal, SplitTrackingBadge, TransferModal},

  mixins: [Dates, Forms],

  methods: {
    ...mapActions("ticketViewer", {
      reloadTicket: "reloadTicket",
    }),

    toggleCollapsed() {
      this.collapsed = !this.collapsed;
    },

    openBookmarkModal() {
      this.$bvModal.show("bookmark-modal-TicketWhatsappLog-" + this.whatsappLog.id);
    },

    openTransferModal() {
      this.$bvModal.show(`transfer-modal-whatsapp-log-${this.whatsappLog.id}`);
    },

    handleTransfer({ newTicketId }) {
      // Navigate to new ticket after successful transfer
      this.$router.push(`/tickets/${newTicketId}`);
    },

    requestDelete() {
      let vm = this;
      swal({
        title: "Are you sure you want to delete this note?",
        text: "This cannot be undone.",
        icon: "warning",
        buttons: {
          cancel: true,
          confirm: "Delete",
        },
        dangerMode: true,
      }).then((shouldDelete) => {
        if (shouldDelete) {
          vm.delete();
        }
      });
    },

    delete() {
      let vm = this;
      vm.isSaving = true;
      axios
        .delete(
          API_BASE + "/tickets/" + this.ticket.id + "/whatsapp/" + this.whatsappLog.id
        )
        .then((r) => {
          vm.isSaving = false;
          vm.reloadTicket();
          this.$notify.success({
            title: "Success!",
            message: "Note deleted successfully",
          });
        })
        .catch((e) => {
          vm.isSaving = false;
          vm.notifyErrors(e);
        });
    },

    openImage(url) {
      window.open(url, '_blank');
    },

    isPdf(url) {
      if (!url) return false;
      return url.toLowerCase().endsWith('.pdf') || url.toLowerCase().includes('.pdf?');
    },

    toggleIncidentReportInclusion() {
      let vm = this;
      vm.isSaving = true;
      vm.serverErrors = [];
      
      axios
        .post(
          API_BASE + "/tickets/" + this.ticket.id + "/whatsapp/" + this.whatsappLog.id + "/toggle-include-in-incident-report"
        )
        .then((r) => {
          vm.isSaving = false;
          // Update the local whatsappLog with the response data
          vm.whatsappLog.include_in_incident_report = r.data.include_in_incident_report;
          if (r.data.incident_report_timestamp) {
            vm.whatsappLog.incident_report_timestamp = r.data.incident_report_timestamp;
          }
          
          this.$notify.success({
            title: "Success!",
            message: `WhatsApp message ${r.data.include_in_incident_report ? 'included' : 'excluded'} from incident report`,
          });
        })
        .catch((e) => {
          vm.isSaving = false;
          vm.notifyErrors(e);
        });
    },
  },
};
</script>

<style lang="scss" scoped>
.card {
  background-image: linear-gradient(#fef1e1, #fef1e1);
}

.cursor-pointer {
  cursor: pointer;
}

.media-preview {
  margin-top: 10px;
}

.media-item {
  display: inline-block;
}

.pdf-icon-container {
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  padding: 10px;
  border: 1px solid #dee2e6;
  border-radius: 0.25rem;
  background-color: #f8f9fa;
  transition: background-color 0.2s;
}

.pdf-icon-container:hover {
  background-color: #e9ecef;
}

.tl-r-message-body {
  white-space: pre-line;
}

// Incident Report Badge Styles
.badge {
  border-radius: 12px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  transition: all 0.2s ease;
  
  &.badge-success {
    background-color: #28a745;
    color: white;
    
    &:hover {
      background-color: #218838;
    }
  }
  
  &.badge-secondary {
    background-color: #6c757d;
    color: white;
    
    &:hover {
      background-color: #545b62;
    }
  }
}
</style>
